import type { FormsApiClient } from '../api';
import type { LeadFormPublicConfig } from '../types';
import { applyThemeVars, buildCloseButton, buildForm, buildHeader, createHost, resolvePopupStyle, SHARED_PANEL_STYLE, type LeadFormTheme, type RenderHandle } from './shared';

/** Backs both SLIDE_IN and SCROLL_BOX — same corner-card interaction (animates in from a viewport
 * edge, stays anchored while the visitor keeps browsing), just a different default footprint:
 * SLIDE_IN defaults to a slim strip, SCROLL_BOX to a fuller card with room for an image. Either can
 * still override via `theme.boxStyle`. */
const STYLE = `
  ${SHARED_PANEL_STYLE}
  .panel {
    position: fixed; bottom: 20px; z-index: 2147483000;
    transition: transform 0.35s ease, opacity 0.35s ease;
  }
  .panel.bottom-right { right: 20px; transform: translateX(20px); }
  .panel.bottom-left { left: 20px; transform: translateX(-20px); }
  .panel.entered { transform: translateX(0); opacity: 1; }
  .panel.slim { width: min(320px, calc(100vw - 40px)); padding: 16px; }
  .panel.full { width: min(380px, calc(100vw - 40px)); }
`;

export function renderCornerCard(
  form: LeadFormPublicConfig,
  client: FormsApiClient,
  onSubmit: (data: Record<string, unknown>) => Promise<boolean>,
  defaultBoxStyle: 'slim' | 'full',
): RenderHandle {
  const theme = form.theme as LeadFormTheme;
  const resolved = resolvePopupStyle(theme);
  const { host, root } = createHost(STYLE);

  const boxStyle = theme.boxStyle ?? defaultBoxStyle;
  const corner = theme.corner ?? 'bottom-right';

  const panel = document.createElement('div');
  panel.className = `panel ${corner} ${boxStyle}`;
  panel.style.opacity = '0';
  applyThemeVars(panel, resolved);

  panel.appendChild(buildCloseButton(close));
  if (boxStyle === 'full') {
    for (const el of buildHeader(form, client, panel)) {
      panel.appendChild(el);
    }
  } else if (theme.headline) {
    const headline = document.createElement('p');
    headline.className = 'headline';
    headline.textContent = theme.headline;
    panel.appendChild(headline);
  }
  panel.appendChild(buildForm(form.fields, theme.ctaLabel || 'Submit', onSubmit, close));

  root.appendChild(panel);
  // Two rAFs: one to let the initial (offscreen, opacity 0) state actually paint, then one to
  // trigger the transition — a single rAF right after append can still coalesce with the initial
  // layout in some browsers and skip the animation entirely.
  requestAnimationFrame(() => requestAnimationFrame(() => panel.classList.add('entered')));

  function close() {
    host.remove();
  }

  return { close };
}
